import React, { useCallback, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDropzone } from 'react-dropzone'
import ModalCrop from './modal-cropper'
import { BoxIntro, Container, ErrorMessage, Icon, Image, ImageBox, Label, RemoveButton, TextIntro, ZoneContainer } from './style'
import iconCloud from '@assets/images/icon-cloud-upload.svg'
import iconDelete from '@assets/images/icon-delete.svg'

const Dropzone = ({ id, className, label, textIntro, value, onChange }) => {
  const [fileName, setFileName] = useState()
  const [fileUploaded, setFileUploaded] = useState()
  const [fileCropped, setFileCropped] = useState(value)
  const [isModalCropped, setIsModalCropped] = useState(false)
  const [isFileSizeError, setIsFileSizeError] = useState(false)
  const [isFileTypeError, setIsFileTypeError] = useState(false)
  const [isLoadError, setIsLoadError] = useState(false)
  const [isAbortLoad, setIsAbortLoad] = useState(false)

  const onModalClose = () => setIsModalCropped(false)

  const setImageCropped = data => {
    setFileCropped(data)
    setIsModalCropped(false)
  }

  const MegaByte = 1048576

  const fileConfig = {
    acceptedTypes: ['image/gif', 'image/png', 'image/jpeg', 'image/svg+xml'],
    maxSize: 2 * MegaByte,
  }

  const onDrop = useCallback(acceptedFiles => {
    acceptedFiles.forEach(file => {
      const fileSizeIsvalid = () => {
        if (file.size < fileConfig.maxSize) {
          setIsFileSizeError(false)
          return true
        } else {
          setIsFileSizeError(true)
          return false
        }
      }

      const fileTypeIsValid = () => {
        const isValid = fileConfig.acceptedTypes.some(item => item === file.type)
        setIsFileTypeError(!isValid)
        return isValid
      }

      const reader = new FileReader()
      reader.onabort = () => setIsAbortLoad(true)
      reader.onerror = () => setIsLoadError(true)
      reader.readAsDataURL(file) //base64

      reader.onload = () => {
        if (fileTypeIsValid() && fileSizeIsvalid()) {
          setFileName(file.name)
          setFileUploaded(reader.result)
          setIsModalCropped(true)
        }
      }
    })
  }, [])

  const handleRemove = () => setFileCropped('')

  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  useEffect(() => {
    if (onChange) onChange(fileCropped, fileName)
  }, [fileCropped])

  return (
    <Container className={className}>
      <Label htmlFor={id}>{label}</Label>
      {fileCropped ? (
        <ZoneContainer>
          <ImageBox>
            <Image src={fileCropped} />
          </ImageBox>

          <RemoveButton type="button" onClick={handleRemove}>
            <Icon src={iconDelete} />
            <span>Remover</span>
          </RemoveButton>
        </ZoneContainer>
      ) : (
        <ZoneContainer className="clickable" {...getRootProps()}>
          <input id={id} {...getInputProps()} />
          <BoxIntro>
            <Icon src={iconCloud} />
            <TextIntro>{textIntro}</TextIntro>
          </BoxIntro>
          {isFileSizeError && <ErrorMessage>Use arquivos com menos de {fileConfig.maxSize / MegaByte}MB</ErrorMessage>}
          {isFileTypeError && <ErrorMessage>Use apenas arquivos de images como: JPG, PNG, GIF ou SVG</ErrorMessage>}
          {isLoadError && <ErrorMessage>Erro ao carregar o arquivo</ErrorMessage>}
          {isAbortLoad && <ErrorMessage>O carregamento foi interrompido</ErrorMessage>}
        </ZoneContainer>
      )}
      <ModalCrop onModalClose={onModalClose} isOpened={isModalCropped} setImageCropped={setImageCropped} fileUploaded={fileUploaded} />
    </Container>
  )
}

Dropzone.displayName = 'Dropzone'
Dropzone.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  textIntro: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
}

export default Dropzone
